import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { X, Plus, Trash2 } from 'lucide-react';
import { refreshJobs, refreshArchiveJobs } from '../../redux/features/jobSlice';
import { getSpareParts } from '../../redux/features/sparePartSlice';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ReportEditForm = ({ job, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { items: spareParts } = useSelector((state) => state.spareParts);
  
  const [loading, setLoading] = useState(false);
  const [serviceCharged, setServiceCharged] = useState('');
  const [reversiCost, setReversiCost] = useState(0);
  const [additionalMaterialsCost, setAdditionalMaterialsCost] = useState(0);
  const [amortizationCost, setAmortizationCost] = useState('');
  const [usedSpareParts, setUsedSpareParts] = useState([]);
  const [additionalMaterials, setAdditionalMaterials] = useState([]);
  const [availableSpareParts, setAvailableSpareParts] = useState([]);

  // Initialize form with existing report data
  useEffect(() => {
    if (job?.report) {
      const report = job.report;
      
      // Set service charged
      setServiceCharged(report.serviceCharged || '');
      
      // Set costs
      setReversiCost(report.reversiCost || 0);
      setAdditionalMaterialsCost(report.additionalMaterialsCost || 0);
      setAmortizationCost(report.amortizationCost || '');
      
      // Set used spare parts
      if (Array.isArray(report.usedSpareParts)) {
        setUsedSpareParts(report.usedSpareParts.map(part => ({
          _id: part._id || part.id || '',
          name: part.name || '',
          code: part.code || '',
          price: typeof part.price === 'number' ? part.price : parseFloat(part.price) || 0,
          quantity: typeof part.quantity === 'number' ? part.quantity : parseFloat(part.quantity) || 1
        })));
      } else {
        setUsedSpareParts([]);
      }
      
      // Set additional materials
      if (Array.isArray(report.additionalMaterials)) {
        setAdditionalMaterials(report.additionalMaterials.map(material => ({
          name: material.name || '',
          price: typeof material.price === 'number' ? material.price : parseFloat(material.price) || 0
        })));
      } else {
        setAdditionalMaterials([]);
      }
    }
  }, [job]);

  // Fetch spare parts
  useEffect(() => {
    dispatch(getSpareParts());
  }, [dispatch]);

  // Update available spare parts when spareParts changes
  useEffect(() => {
    if (spareParts && Array.isArray(spareParts)) {
      setAvailableSpareParts(spareParts);
    }
  }, [spareParts]);

  // Calculate reversiCost from usedSpareParts
  useEffect(() => {
    const calculated = usedSpareParts.reduce((sum, part) => {
      const price = typeof part.price === 'number' ? part.price : parseFloat(part.price) || 0;
      const quantity = typeof part.quantity === 'number' ? part.quantity : parseFloat(part.quantity) || 1;
      return sum + (price * quantity);
    }, 0);
    setReversiCost(calculated);
  }, [usedSpareParts]);

  // Calculate additionalMaterialsCost from additionalMaterials
  useEffect(() => {
    const calculated = additionalMaterials.reduce((sum, material) => {
      const price = typeof material.price === 'number' ? material.price : parseFloat(material.price) || 0;
      return sum + price;
    }, 0);
    setAdditionalMaterialsCost(calculated);
  }, [additionalMaterials]);

  const handleAddSparePart = () => {
    const newPart = {
      _id: '',
      name: '',
      code: '',
      price: 0,
      quantity: 1
    };
    setUsedSpareParts([...usedSpareParts, newPart]);
  };

  const handleUpdateSparePart = (index, field, value) => {
    setUsedSpareParts(prev => prev.map((part, i) => {
      if (i === index) {
        if (field === '_id' || field === 'sparePartId') {
          // When selecting a spare part, populate its data
          const selectedSparePart = availableSpareParts.find(sp => (sp._id || sp.id) === value);
          if (selectedSparePart) {
            return {
              _id: selectedSparePart._id || selectedSparePart.id || '',
              name: selectedSparePart.name || '',
              code: selectedSparePart.code || '',
              price: selectedSparePart.price || 0,
              quantity: part.quantity || 1
            };
          }
          // If no match found, just update the _id
          return { ...part, _id: value };
        }
        // For numeric fields, allow empty string during input
        if (field === 'price' || field === 'quantity') {
          // Allow empty string or valid number
          if (value === '' || value === null || value === undefined) {
            return { ...part, [field]: '' };
          }
          const numValue = typeof value === 'number' ? value : (field === 'quantity' ? parseInt(value, 10) : parseFloat(value));
          return { ...part, [field]: isNaN(numValue) ? '' : numValue };
        }
        return { ...part, [field]: value };
      }
      return part;
    }));
  };

  const handleRemoveSparePart = (index) => {
    setUsedSpareParts(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddAdditionalMaterial = () => {
    setAdditionalMaterials([...additionalMaterials, { name: '', price: 0 }]);
  };

  const handleUpdateAdditionalMaterial = (index, field, value) => {
    setAdditionalMaterials(prev => prev.map((material, i) => {
      if (i === index) {
        // For price field, allow empty string during input
        if (field === 'price') {
          // Allow empty string or valid number
          if (value === '' || value === null || value === undefined) {
            return { ...material, price: '' };
          }
          const numValue = typeof value === 'number' ? value : parseFloat(value);
          return { ...material, price: isNaN(numValue) ? '' : numValue };
        }
        return { ...material, [field]: value };
      }
      return material;
    }));
  };

  const handleRemoveAdditionalMaterial = (index) => {
    setAdditionalMaterials(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loading) return;
    
    setLoading(true);
    
    try {
      const token = user?.token;
      
      // Calculate totals - ensure all are numbers
      const amortizationCostNum = typeof amortizationCost === 'number' ? amortizationCost : (amortizationCost === '' || amortizationCost === null || amortizationCost === undefined ? 0 : parseFloat(amortizationCost) || 0);
      const totalCost = reversiCost + additionalMaterialsCost + amortizationCostNum;
      const serviceChargedNum = parseFloat(serviceCharged.toString().replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
      const profit = serviceChargedNum - totalCost;
      
      // Build report payload
      const reportPayload = {
        // Preserve existing report fields
        materialsUsed: job.report?.materialsUsed || '',
        workDescription: job.report?.workDescription || '',
        customerSignature: job.report?.customerSignature || '',
        customerRating: job.report?.customerRating || 10,
        additionalNotes: job.report?.additionalNotes || '',
        // Updated fields
        serviceCharged: serviceCharged.toString(),
        usedSpareParts: usedSpareParts.map(part => ({
          _id: part._id || '',
          name: part.name || '',
          code: part.code || '',
          price: typeof part.price === 'number' ? part.price : parseFloat(part.price) || 0,
          quantity: typeof part.quantity === 'number' ? part.quantity : parseFloat(part.quantity) || 1
        })),
        additionalMaterials: additionalMaterials.map(material => ({
          name: material.name || '',
          price: typeof material.price === 'number' ? material.price : parseFloat(material.price) || 0
        })),
        // Cost fields
        reversiCost: Number(reversiCost) || 0,
        additionalMaterialsCost: Number(additionalMaterialsCost) || 0,
        amortizationCost: Number(amortizationCostNum) || 0,
        totalCost: Number(totalCost) || 0,
        profit: Number(profit) || 0,
        // Preserve time fields
        totalTravelTime: job.report?.totalTravelTime || 0,
        totalWorkTime: job.report?.totalWorkTime || 0,
        // Preserve date fields
        completedAt: job.report?.completedAt || new Date().toISOString(),
        submittedAt: job.report?.submittedAt || new Date().toISOString()
      };
      
      console.log('📤 Updating report payload:', reportPayload);
      
      await axios.post(
        `${API_BASE_URL}/api/jobs/${job._id}/report`,
        reportPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Report podaci su uspešno ažurirani');
      
      await dispatch(refreshJobs());
      await dispatch(refreshArchiveJobs());
      
      if (onSuccess) {
        onSuccess();
      }
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error updating report:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Greška pri ažuriranju report podataka';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for display
  const amortizationCostNum = typeof amortizationCost === 'number' ? amortizationCost : (amortizationCost === '' || amortizationCost === null || amortizationCost === undefined ? 0 : parseFloat(amortizationCost) || 0);
  const totalCost = reversiCost + additionalMaterialsCost + amortizationCostNum;
  const serviceChargedNum = parseFloat(serviceCharged.toString().replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
  const profit = serviceChargedNum - totalCost;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Service Charged */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Naplaćena usluga (RSD)
        </label>
        <input
          type="text"
          value={serviceCharged}
          onChange={(e) => setServiceCharged(e.target.value)}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="0"
        />
      </div>

      {/* Used Spare Parts (Reversi) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-300">
            Materijal sa reversa
          </label>
          <button
            type="button"
            onClick={handleAddSparePart}
            className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
          >
            <Plus className="h-3 w-3" />
            Dodaj deo
          </button>
        </div>
        <div className="space-y-2">
          {usedSpareParts.map((part, index) => (
            <div key={index} className="flex gap-2 items-start bg-gray-800 p-2 rounded-lg">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <div>
                  <select
                    value={part._id || ''}
                    onChange={(e) => handleUpdateSparePart(index, '_id', e.target.value)}
                    className="w-full px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Izaberi deo...</option>
                    {availableSpareParts.map(sp => (
                      <option key={sp._id || sp.id} value={sp._id || sp.id}>
                        {sp.name} {sp.code ? `(${sp.code})` : ''}
                      </option>
                    ))}
                  </select>
                  {part._id && (
                    <div className="mt-1">
                      <input
                        type="text"
                        value={part.name || ''}
                        onChange={(e) => handleUpdateSparePart(index, 'name', e.target.value)}
                        className="w-full px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                        placeholder="Naziv dela"
                      />
                      <input
                        type="text"
                        value={part.code || ''}
                        onChange={(e) => handleUpdateSparePart(index, 'code', e.target.value)}
                        className="w-full px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 mt-1"
                        placeholder="Šifra"
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <input
                    type="number"
                    value={part.price === undefined || part.price === null ? '' : part.price}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        handleUpdateSparePart(index, 'price', '');
                      } else {
                        const numValue = parseFloat(val);
                        handleUpdateSparePart(index, 'price', isNaN(numValue) ? '' : numValue);
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        handleUpdateSparePart(index, 'price', 0);
                      } else {
                        const numValue = parseFloat(val);
                        handleUpdateSparePart(index, 'price', isNaN(numValue) ? 0 : numValue);
                      }
                    }}
                    className="w-full px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="Cena"
                    min="0"
                    step="0.01"
                  />
                  <input
                    type="number"
                    value={part.quantity === undefined || part.quantity === null ? '' : part.quantity}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        handleUpdateSparePart(index, 'quantity', '');
                      } else {
                        const numValue = parseInt(val, 10);
                        handleUpdateSparePart(index, 'quantity', isNaN(numValue) ? '' : numValue);
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        handleUpdateSparePart(index, 'quantity', 1);
                      } else {
                        const numValue = parseInt(val, 10);
                        handleUpdateSparePart(index, 'quantity', isNaN(numValue) ? 1 : numValue);
                      }
                    }}
                    className="w-16 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="Kol"
                    min="1"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveSparePart(index)}
                className="p-1 text-red-400 hover:text-red-300"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {usedSpareParts.length === 0 && (
            <div className="text-xs text-gray-400 text-center py-2">
              Nema dodatih delova
            </div>
          )}
        </div>
        <div className="mt-2 text-xs text-gray-400">
          Ukupno materijal sa reversa: <span className="text-white font-semibold">{reversiCost.toLocaleString('sr-RS')} RSD</span>
        </div>
      </div>

      {/* Additional Materials */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-300">
            Dodatni materijal
          </label>
          <button
            type="button"
            onClick={handleAddAdditionalMaterial}
            className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
          >
            <Plus className="h-3 w-3" />
            Dodaj materijal
          </button>
        </div>
        <div className="space-y-2">
          {additionalMaterials.map((material, index) => (
            <div key={index} className="flex gap-2 items-start bg-gray-800 p-2 rounded-lg">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={material.name || ''}
                  onChange={(e) => handleUpdateAdditionalMaterial(index, 'name', e.target.value)}
                  className="w-full px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Naziv materijala"
                />
                  <input
                    type="number"
                    value={material.price === undefined || material.price === null ? '' : material.price}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        handleUpdateAdditionalMaterial(index, 'price', '');
                      } else {
                        const numValue = parseFloat(val);
                        handleUpdateAdditionalMaterial(index, 'price', isNaN(numValue) ? '' : numValue);
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        handleUpdateAdditionalMaterial(index, 'price', 0);
                      } else {
                        const numValue = parseFloat(val);
                        handleUpdateAdditionalMaterial(index, 'price', isNaN(numValue) ? 0 : numValue);
                      }
                    }}
                    className="w-full px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="Cena"
                    min="0"
                    step="0.01"
                  />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveAdditionalMaterial(index)}
                className="p-1 text-red-400 hover:text-red-300"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {additionalMaterials.length === 0 && (
            <div className="text-xs text-gray-400 text-center py-2">
              Nema dodatih materijala
            </div>
          )}
        </div>
        <div className="mt-2 text-xs text-gray-400">
          Ukupno dodatni materijal: <span className="text-white font-semibold">{additionalMaterialsCost.toLocaleString('sr-RS')} RSD</span>
        </div>
      </div>

      {/* Amortization Cost */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Amortizacija (RSD)
        </label>
        <input
          type="number"
          value={amortizationCost}
          onChange={(e) => {
            const val = e.target.value;
            // Allow empty string during input
            if (val === '' || val === null || val === undefined) {
              setAmortizationCost('');
            } else {
              const numValue = parseFloat(val);
              setAmortizationCost(isNaN(numValue) ? '' : numValue);
            }
          }}
          onBlur={(e) => {
            // On blur, ensure it's a number (default to 0 if empty)
            const val = e.target.value;
            if (val === '' || val === null || val === undefined) {
              setAmortizationCost(0);
            } else {
              const numValue = parseFloat(val);
              setAmortizationCost(isNaN(numValue) ? 0 : numValue);
            }
          }}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="0"
          min="0"
          step="0.01"
        />
      </div>

      {/* Summary */}
      <div className="bg-gray-800 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Ukupan trošak:</span>
          <span className="text-red-400 font-semibold">{totalCost.toLocaleString('sr-RS')} RSD</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Naplaćena usluga:</span>
          <span className="text-green-400 font-semibold">{serviceChargedNum.toLocaleString('sr-RS')} RSD</span>
        </div>
        <div className="flex justify-between text-base pt-2 border-t border-gray-700">
          <span className="text-gray-300 font-medium">Čista zarada:</span>
          <span className={`font-bold ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {profit.toLocaleString('sr-RS')} RSD
          </span>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600"
          disabled={loading}
        >
          Otkaži
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Čuvanje...' : 'Sačuvaj izmene'}
        </button>
      </div>
    </form>
  );
};

export default ReportEditForm;
